import { useEffect, useState } from 'react';
import { api } from '@/api/client';
import { ConnectorAuthModal } from './ConnectorAuthModal';
import { PickerGlyph } from './pickerIcons';

export interface ConnectorRow {
  id: string;
  name: string;
  version?: string;
  publisher?: string;
  icon?: string;
  description?: string;
  status: 'installed' | 'core' | 'disabled' | 'coming-soon';
  counts?: { wrappers?: number; tools?: number; kbDocuments?: number };
}

interface ConnectorListResponse { data: ConnectorRow[] }

interface Identity { provider: string; email?: string; lastSeenAt?: string }
interface MeResponse { identities?: Identity[] }

// External connectors - drives the Authorize button + linked badge per
// connector id. `loginFlow` controls what the Authorize click opens:
//   'direct' - inline email/password form (single-step, Lumara)
//   'otp'    - modal email/password then OTP (two-step, ESG Caffe)
//   undefined - redirect to OAuth consent page (DataCaffe)
// `comingSoon` - no auth bridge on the server yet (the backend PROVIDERS
//   registry has no entry), so Authorize would 404. Show a "soon" pill and
//   hide the Authorize/toggle until the bridge ships. Drop the flag once the
//   server endpoint exists.
const EXTERNAL_CONNECTORS: Record<string, { label: string; loginFlow?: 'direct' | 'otp'; comingSoon?: boolean }> = {
  datacaffe:     { label: 'DataCaffe', comingSoon: true },
  'esg-caffe':   { label: 'ESG Caffe', loginFlow: 'otp' },
  electioncaffe: { label: 'Election Caffe', comingSoon: true },
  lumara:        { label: 'Lumara', loginFlow: 'direct' },
};

// Display order for the "Add a solution" flyout: connectable products first
// (ESG, Lumara), then the coming-soon ones (Election Caffe, DataCaffe). Ids not
// listed fall to the end, keeping their relative (alphabetical) order.
const SOLUTION_ORDER = ['esg-caffe', 'lumara', 'electioncaffe', 'datacaffe'];
const solutionRank = (id: string): number => {
  const i = SOLUTION_ORDER.indexOf(id);
  return i === -1 ? SOLUTION_ORDER.length : i;
};

// Solutions with no backend connector yet — display-only "coming soon" rows in
// the "Add a solution" flyout. Each row's arrow will link to that solution's
// landing page once those pages exist (href TBD).
const PLACEHOLDER_SOLUTIONS: { id: string; name: string }[] = [
  { id: 'command-iq', name: 'Command IQ' },
  { id: 'insurance-caffe', name: 'Insurance Caffe' },
  { id: 'ngo-caffe', name: 'NGO Caffe' },
  { id: 'vibecoding', name: 'Vibecoding' },
];

// Small up-right arrow — marks each solution row as linking out to its landing
// page. Visual only for now; becomes an anchor once the landing URLs are wired.
function LinkArrow() {
  return (
    <svg
      className="snav__pop-item__arrow-svg"
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M7 17 17 7" />
      <path d="M8 7h9v9" />
    </svg>
  );
}

// Set of connectors whose Authorize click opens an in-page sign-in
// surface instead of redirecting away. (Both `direct` and `otp` flows
// stay in-page; only OAuth providers leave.)
const LOGIN_DIRECT = new Set(
  Object.entries(EXTERNAL_CONNECTORS)
    .filter(([, v]) => v.loginFlow === 'direct' || v.loginFlow === 'otp')
    .map(([k]) => k),
);

type AuthState = 'na' | 'authorized' | 'unauthorized' | 'coming-soon';

/**
 * Connectors sidebar panel — direct port of the legacy `sb-panel`
 * (data-panel="connectors") from web-ui/public/index.html.
 *
 * Uses the same connector-card CSS (in styles/legacy.css) so cards
 * look identical to the legacy SPA: emoji icon, title, version,
 * description, counts, and toggle / authorize / authorized pill.
 */
export function ConnectorsPanel({ hovered, activeConnectorId }: { hovered: boolean; activeConnectorId?: string | null }) {
  const [rows, setRows] = useState<ConnectorRow[] | null>(null);
  const [linked, setLinked] = useState<Set<string>>(new Set());
  // Connected account email per provider (the product login the connector is
  // authorized as) — shown in the manage modal so the user can see WHICH
  // account is linked before re-authorizing or disconnecting.
  const [linkedEmails, setLinkedEmails] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  // Which connector's sign-in flow is open. null = closed.
  const [openLoginFor, setOpenLoginFor] = useState<string | null>(null);
  // Which connected connector is being managed (re-authorize / disconnect).
  const [manageId, setManageId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load(): Promise<void> {
    setError(null);
    try {
      const [list, me] = await Promise.all([
        api.get<ConnectorListResponse>('/api/connectors'),
        api.get<MeResponse>('/api/auth/me'),
      ]);
      const ordered = [...list.data].sort((a, b) => {
        if ((a.status === 'core') !== (b.status === 'core')) return a.status === 'core' ? -1 : 1;
        return (a.id || '').localeCompare(b.id || '');
      });
      setRows(ordered);
      const idents = me.identities ?? [];
      setLinked(new Set(idents.map((i) => i.provider)));
      // A provider can have several identity rows (the user linked more than one
      // account over time); the ACTIVE one is the most-recently-seen. Keep that
      // email per provider so the modal shows the account currently in use.
      const byProvider = new Map<string, Identity>();
      for (const i of idents) {
        const prev = byProvider.get(i.provider);
        if (!prev || (i.lastSeenAt ?? '') > (prev.lastSeenAt ?? '')) byProvider.set(i.provider, i);
      }
      setLinkedEmails(new Map([...byProvider].map(([p, v]) => [p, v.email ?? ''])));
    } catch (e) {
      setError((e as Error).message || 'Could not load connectors');
    }
  }

  useEffect(() => { void load(); }, []);

  // Esc closes the manage modal.
  useEffect(() => {
    if (!manageId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setManageId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [manageId]);

  function authState(id: string): AuthState {
    const ext = EXTERNAL_CONNECTORS[id];
    if (!ext) return 'na';
    if (linked.has(id)) return 'authorized';
    // No server bridge yet → can't authorize; surface as "coming soon"
    // rather than a dead Authorize button that 404s.
    if (ext.comingSoon) return 'coming-soon';
    return 'unauthorized';
  }

  async function startAuthorize(c: ConnectorRow): Promise<void> {
    // Login-direct connectors (Lumara today) drop the OAuth chooser and
    // show an inline email/password form right below the card head.
    if (LOGIN_DIRECT.has(c.id)) {
      setOpenLoginFor(c.id);
      return;
    }
    try {
      const r = await api.post<{ authorizeUrl: string }>(
        `/api/auth/connectors/${c.id}/init`,
        { returnTo: window.location.pathname, origin: window.location.origin },
      );
      window.location.href = r.authorizeUrl;
    } catch (e) {
      setError((e as Error).message || `Couldn't start ${c.name} authorize`);
    }
  }

  // Re-run the sign-in flow for an already-connected connector (refresh tokens
  // / switch account). Same path as the initial authorize.
  async function reauthorize(c: ConnectorRow): Promise<void> {
    setManageId(null);
    await startAuthorize(c);
  }

  // Drop the stored identity so Brew can no longer fetch the user's data there.
  async function disconnect(c: ConnectorRow): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/api/auth/identities/${c.id}`);
      try {
        const ch = new BroadcastChannel('brew-connector-auth');
        ch.postMessage({ type: 'connector-unlinked', provider: c.id });
        ch.close();
      } catch { /* not critical */ }
      setManageId(null);
      await load();
    } catch (e) {
      setError((e as Error).message || `Couldn't disconnect ${c.name}`);
    } finally {
      setBusy(false);
    }
  }

  // Active packs — core/enabled plus any authorized external. These pin under
  // the section. Everything else (unauthorized / coming-soon) is "available"
  // and lives in the hover flyout until the user activates it.
  const activeRows = (rows ?? []).filter((c) => {
    const on = c.status === 'core' || c.status === 'installed';
    const st = authState(c.id);
    return on && st !== 'unauthorized' && st !== 'coming-soon';
  });
  const availableRows = (rows ?? [])
    .filter((c) => !activeRows.includes(c))
    .sort((a, b) => solutionRank(a.id) - solutionRank(b.id));
  // Brew Core is the always-on base pack — keep it in activeRows (so it's never
  // offered in the "add" flyout) but don't surface it in the pinned list; only
  // *other* active connectors are worth showing there.
  const pinnedRows = activeRows.filter((c) => c.status !== 'core');
  const loginConn = openLoginFor ? (rows ?? []).find((c) => c.id === openLoginFor) ?? null : null;
  const loginFlow = openLoginFor ? EXTERNAL_CONNECTORS[openLoginFor]?.loginFlow : undefined;
  const manageConn = manageId ? (rows ?? []).find((c) => c.id === manageId) ?? null : null;

  return (
    <>
      {/* Active packs (excluding the always-on Brew Core) — pinned under the
          section heading. */}
      {pinnedRows.length > 0 && (
        <div className="snav__pinned">
          {pinnedRows.map((c) => {
            const isActive = !!activeConnectorId && c.id === activeConnectorId;
            return (
              <button
                key={c.id}
                type="button"
                className="snav__pinned-item"
                data-active={isActive ? 'true' : undefined}
                aria-current={isActive ? 'true' : undefined}
                data-tip={isActive ? `${c.name} — active · manage` : `Manage ${c.name}`}
                onClick={() => setManageId(c.id)}
              >
                <span className="snav__pinned-item__dot" aria-hidden="true" />
                <span className="snav__pinned-item__txt">{c.name}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Hover flyout — the not-yet-active packs as a plain list. Click one to
          start its sign-in; once active it moves to the pinned list above. */}
      {hovered && !manageId && (
        <div className="snav__pop" role="menu">
          <div className="snav__pop-title">Add a solution</div>
          {!rows && !error && <div className="snav__pop-msg">Loading…</div>}
          {error && <div className="snav__pop-msg snav__pop-msg--err">{error}</div>}
          {rows && availableRows.length === 0 && <div className="snav__pop-msg">All packs active.</div>}
          {availableRows.map((c) => {
            const soon = authState(c.id) === 'coming-soon';
            return (
              <button
                key={c.id}
                type="button"
                className="snav__pop-item"
                disabled={soon}
                onClick={() => { if (!soon) void startAuthorize(c); }}
                data-tip={soon ? 'Coming soon' : `Connect ${c.name}`}
              >
                <PickerGlyph icon={c.icon ?? null} label={c.name} size={26} />
                <span className="snav__pop-item__name">{c.name}</span>
                {soon && <span className="snav__pop-item__tag">soon</span>}
                <span className="snav__pop-item__arrow"><LinkArrow /></span>
              </button>
            );
          })}
          {/* Coming-soon solutions that don't have a backend connector yet. */}
          {PLACEHOLDER_SOLUTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              className="snav__pop-item"
              disabled
              data-tip="Coming soon"
            >
              <PickerGlyph icon={null} label={s.name} size={26} />
              <span className="snav__pop-item__name">{s.name}</span>
              <span className="snav__pop-item__tag">soon</span>
              <span className="snav__pop-item__arrow"><LinkArrow /></span>
            </button>
          ))}
        </div>
      )}

      {/* Sign-in surface — rendered at panel root so it survives the flyout
          closing. One modal for every connector; `flow` decides whether it's a
          single-step (direct) or two-step OTP sign-in. */}
      {loginConn && (loginFlow === 'otp' || loginFlow === 'direct') && (
        <ConnectorAuthModal
          productLabel={loginConn.name}
          providerId={loginConn.id}
          productIcon={loginConn.icon ?? null}
          flow={loginFlow}
          onCancel={() => setOpenLoginFor(null)}
          onSuccess={async () => {
            const provider = openLoginFor;
            setOpenLoginFor(null);
            // Tell other surfaces (the chat's Solution selector) a product was
            // linked, so it can offer it without a reload.
            try {
              const ch = new BroadcastChannel('brew-connector-auth');
              ch.postMessage({ type: 'connector-linked', provider });
              ch.close();
            } catch { /* not critical */ }
            await load();
          }}
        />
      )}

      {/* Manage a connected connector — re-authorize or disconnect. Reuses the
          shared connector-modal shell (centered logo + actions). */}
      {manageConn && (
        <div
          className="connector-otp-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={`Manage ${manageConn.name}`}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setManageId(null); }}
        >
          <div className="connector-otp-modal connector-manage" data-provider={manageConn.id}>
            <button
              type="button"
              aria-label="Close"
              className="connector-otp-modal__close"
              onClick={() => setManageId(null)}
            >
              ✕
            </button>
            <div className="connector-otp-modal__logo">
              <PickerGlyph icon={manageConn.icon ?? null} label={manageConn.name} size={96} />
            </div>
            <div className="connector-manage__name">{manageConn.name}</div>
            {linkedEmails.get(manageConn.id) ? (
              <div className="connector-manage__account">
                <span className="connector-manage__status-dot" aria-hidden="true" />
                <span className="connector-manage__account-label">Connected as</span>
                <span className="connector-manage__email" title={linkedEmails.get(manageConn.id)}>
                  {linkedEmails.get(manageConn.id)}
                </span>
              </div>
            ) : (
              <div className="connector-manage__account">
                <span className="connector-manage__status-dot" aria-hidden="true" />
                <span className="connector-manage__account-label">Connected</span>
              </div>
            )}
            {error && <div className="connector-otp-modal__err">{error}</div>}
            <button
              type="button"
              className="connector-otp-modal__submit"
              disabled={busy}
              onClick={() => void reauthorize(manageConn)}
            >
              Re-authorize
            </button>
            <button
              type="button"
              className="connector-manage__disconnect"
              disabled={busy}
              onClick={() => void disconnect(manageConn)}
            >
              {busy ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
